// ---------------------------------------------------------------------------
// MKT-017 Phase 2 — White-Label Storefront Listings Schema
// ---------------------------------------------------------------------------
// Story MKT-017: "White-label marketplace lets an agency sell its own
// solutions under its brand."
//
// Phase 1 shipped the pure-function classifier in
// `packages/marketing/src/compliance/mkt-017-white-label-storefront-classifier.ts`.
// Phase 5 shipped the CQRS event vocabulary in
// `mkt-017-white-label-storefront-event-catalog.ts`.
//
// This Phase 2 file persists the listing draft + classifier output so the
// future submit / admin-review / publish routes can upsert a deterministic
// row instead of recomputing everything from request payloads alone.
//
// Design rules locked here:
// - One row per `(tenant_id, listing_key)` to make submit idempotent.
// - `status`, `kind`, and `decision_source` reuse the locked Phase 5
//   vocabularies by reference.
// - Pricing is integer minor units only. No floating point money.
// - `billing_mode`, `customisation_policy`, `currency`, `blocked_rules`,
//   and `branding_overrides` are closed vocabularies pinned to the
//   classifier contract so route/schema drift fails closed.
// - Artifact ids remain `text[]` at this phase because the row contract
//   only locks platform-resident artifact identifiers, not cross-table FK
//   homogeneity.
// ---------------------------------------------------------------------------

import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";

import {
  STOREFRONT_LISTING_DECISION_SOURCES,
  STOREFRONT_LISTING_KIND_VOCAB,
  STOREFRONT_LISTING_VERDICT_VOCAB,
} from "./mkt-017-white-label-storefront-event-catalog";

const tz = { withTimezone: true } as const;

export const AGENCY_WHITE_LABEL_STOREFRONT_STATUSES =
  STOREFRONT_LISTING_VERDICT_VOCAB;
export type AgencyWhiteLabelStorefrontStatus =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_STATUSES)[number];

export function isAgencyWhiteLabelStorefrontStatus(
  value: unknown,
): value is AgencyWhiteLabelStorefrontStatus {
  return (
    typeof value === "string" &&
    (AGENCY_WHITE_LABEL_STOREFRONT_STATUSES as readonly string[]).includes(
      value,
    )
  );
}

export const AGENCY_WHITE_LABEL_STOREFRONT_KINDS =
  STOREFRONT_LISTING_KIND_VOCAB;
export type AgencyWhiteLabelStorefrontKind =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_KINDS)[number];

export const AGENCY_WHITE_LABEL_STOREFRONT_DECISION_SOURCES =
  STOREFRONT_LISTING_DECISION_SOURCES;
export type AgencyWhiteLabelStorefrontDecisionSource =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_DECISION_SOURCES)[number];

export const AGENCY_WHITE_LABEL_STOREFRONT_BILLING_MODES = [
  "one_time",
  "subscription_monthly",
  "subscription_annual",
  "metered",
  "free",
] as const;
export type AgencyWhiteLabelStorefrontBillingMode =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_BILLING_MODES)[number];

export const AGENCY_WHITE_LABEL_STOREFRONT_CUSTOMISATION_POLICIES = [
  "locked",
  "branding_only",
  "full_override",
] as const;
export type AgencyWhiteLabelStorefrontCustomisationPolicy =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_CUSTOMISATION_POLICIES)[number];

export const AGENCY_WHITE_LABEL_STOREFRONT_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "RON",
  "AUD",
  "CAD",
] as const;
export type AgencyWhiteLabelStorefrontCurrency =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_CURRENCIES)[number];

export const AGENCY_WHITE_LABEL_STOREFRONT_BLOCKER_VOCAB = [
  "seller_not_agency",
  "seller_missing_white_label_entitlement",
  "agency_id_missing",
  "name_empty",
  "name_too_long",
  "summary_empty",
  "summary_too_long",
  "artifacts_empty",
  "artifact_id_empty",
  "artifact_kind_unknown",
  "price_negative",
  "price_currency_invalid",
  "billing_mode_invalid",
  "billing_mode_recurring_missing_interval",
  "scope_outside_owner_agency",
  "kind_mismatch",
  "branding_override_disallowed",
  "duplicate_artifact_id",
] as const;
export type AgencyWhiteLabelStorefrontBlocker =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_BLOCKER_VOCAB)[number];

export const AGENCY_WHITE_LABEL_STOREFRONT_BRANDING_OVERRIDE_VOCAB = [
  "logo_url",
  "primary_color",
  "secondary_color",
  "accent_color",
  "company_name",
  "support_email",
  "support_url",
] as const;
export type AgencyWhiteLabelStorefrontBrandingOverride =
  (typeof AGENCY_WHITE_LABEL_STOREFRONT_BRANDING_OVERRIDE_VOCAB)[number];

export const agencyWhiteLabelStorefrontListings = pgTable(
  "agency_white_label_storefront_listings",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    agencyId: text("agency_id").notNull(),
    listingKey: text("listing_key").notNull(),
    listingName: text("listing_name").notNull(),
    summary: text("summary").notNull(),
    kind: text("kind").notNull(),
    status: text("status").notNull(),
    decisionSource: text("decision_source")
      .notNull()
      .default("classifier"),
    reviewerUserId: uuid("reviewer_user_id"),
    priceAmountCents: integer("price_amount_cents").notNull(),
    currency: text("currency").notNull(),
    billingMode: text("billing_mode").notNull(),
    billingIntervalDays: integer("billing_interval_days"),
    customisationPolicy: text("customisation_policy")
      .notNull()
      .default("branding_only"),
    brandingOverrides: text("branding_overrides")
      .array()
      .notNull()
      .default(sql`'{}'`),
    workflowIds: text("workflow_ids").array().notNull().default(sql`'{}'`),
    templateIds: text("template_ids").array().notNull().default(sql`'{}'`),
    reportIds: text("report_ids").array().notNull().default(sql`'{}'`),
    serviceOfferingIds: text("service_offering_ids")
      .array()
      .notNull()
      .default(sql`'{}'`),
    blockedRules: text("blocked_rules").array().notNull().default(sql`'{}'`),
    publishedAt: timestamp("published_at", tz),
    publishedBy: uuid("published_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("agency_white_label_storefront_listings_tenant_key_uq").on(
      table.tenantId,
      table.listingKey,
    ),
    index("agency_white_label_storefront_listings_tenant_status_idx").on(
      table.tenantId,
      table.status,
    ),
    index("agency_white_label_storefront_listings_agency_status_idx").on(
      table.agencyId,
      table.status,
    ),
    check(
      "agency_white_label_storefront_listings_status_ck",
      sql`${table.status} IN (${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_STATUSES.map((status) => sql`${status}`),
        sql`, `,
      )})`,
    ),
    check(
      "agency_white_label_storefront_listings_kind_ck",
      sql`${table.kind} IN (${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_KINDS.map((kind) => sql`${kind}`),
        sql`, `,
      )})`,
    ),
    check(
      "agency_white_label_storefront_listings_decision_source_ck",
      sql`${table.decisionSource} IN (${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_DECISION_SOURCES.map(
          (source) => sql`${source}`,
        ),
        sql`, `,
      )})`,
    ),
    check(
      "agency_white_label_storefront_listings_billing_mode_ck",
      sql`${table.billingMode} IN (${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_BILLING_MODES.map((mode) => sql`${mode}`),
        sql`, `,
      )})`,
    ),
    check(
      "agency_white_label_storefront_listings_customisation_policy_ck",
      sql`${table.customisationPolicy} IN (${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_CUSTOMISATION_POLICIES.map(
          (policy) => sql`${policy}`,
        ),
        sql`, `,
      )})`,
    ),
    check(
      "agency_white_label_storefront_listings_currency_ck",
      sql`${table.currency} IN (${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_CURRENCIES.map((currency) => sql`${currency}`),
        sql`, `,
      )})`,
    ),
    check(
      "agency_white_label_storefront_listings_blocked_rules_ck",
      sql`${table.blockedRules} <@ ARRAY[${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_BLOCKER_VOCAB.map((rule) => sql`${rule}`),
        sql`, `,
      )}]::text[]`,
    ),
    check(
      "agency_white_label_storefront_listings_branding_overrides_ck",
      sql`${table.brandingOverrides} <@ ARRAY[${sql.join(
        AGENCY_WHITE_LABEL_STOREFRONT_BRANDING_OVERRIDE_VOCAB.map(
          (field) => sql`${field}`,
        ),
        sql`, `,
      )}]::text[]`,
    ),
    check(
      "agency_white_label_storefront_listings_price_nonneg_ck",
      sql`${table.priceAmountCents} >= 0`,
    ),
    check(
      "agency_white_label_storefront_listings_billing_interval_ck",
      sql`(
        (${table.billingMode} IN ('subscription_monthly', 'subscription_annual')
          AND ${table.billingIntervalDays} IS NOT NULL
          AND ${table.billingIntervalDays} > 0)
        OR (${table.billingMode} NOT IN ('subscription_monthly', 'subscription_annual')
          AND ${table.billingIntervalDays} IS NULL)
      )`,
    ),
    check(
      "agency_white_label_storefront_listings_artifact_presence_ck",
      sql`(
        cardinality(${table.workflowIds})
        + cardinality(${table.templateIds})
        + cardinality(${table.reportIds})
        + cardinality(${table.serviceOfferingIds})
      ) > 0`,
    ),
    check(
      "agency_white_label_storefront_listings_kind_semantics_ck",
      sql`(
        (${table.kind} = 'workflow_bundle'
          AND cardinality(${table.workflowIds}) > 0
          AND cardinality(${table.templateIds}) = 0
          AND cardinality(${table.reportIds}) = 0
          AND cardinality(${table.serviceOfferingIds}) = 0)
        OR (${table.kind} = 'template_bundle'
          AND cardinality(${table.workflowIds}) = 0
          AND cardinality(${table.templateIds}) > 0
          AND cardinality(${table.reportIds}) = 0
          AND cardinality(${table.serviceOfferingIds}) = 0)
        OR (${table.kind} = 'report_bundle'
          AND cardinality(${table.workflowIds}) = 0
          AND cardinality(${table.templateIds}) = 0
          AND cardinality(${table.reportIds}) > 0
          AND cardinality(${table.serviceOfferingIds}) = 0)
        OR (${table.kind} = 'service_offering'
          AND cardinality(${table.workflowIds}) = 0
          AND cardinality(${table.templateIds}) = 0
          AND cardinality(${table.reportIds}) = 0
          AND cardinality(${table.serviceOfferingIds}) > 0)
        OR (${table.kind} = 'mixed_pack'
          AND (
            CASE WHEN cardinality(${table.workflowIds}) > 0 THEN 1 ELSE 0 END
            + CASE WHEN cardinality(${table.templateIds}) > 0 THEN 1 ELSE 0 END
            + CASE WHEN cardinality(${table.reportIds}) > 0 THEN 1 ELSE 0 END
            + CASE WHEN cardinality(${table.serviceOfferingIds}) > 0 THEN 1 ELSE 0 END
          ) >= 2)
      )`,
    ),
    check(
      "agency_white_label_storefront_listings_status_semantics_ck",
      sql`(
        (${table.status} = 'rejected' AND cardinality(${table.blockedRules}) > 0)
        OR (${table.status} IN ('auto_approve', 'needs_review') AND cardinality(${table.blockedRules}) = 0)
      )`,
    ),
    check(
      "agency_white_label_storefront_listings_reviewer_semantics_ck",
      sql`(
        (${table.decisionSource} = 'admin_review' AND ${table.reviewerUserId} IS NOT NULL)
        OR (${table.decisionSource} = 'classifier' AND ${table.reviewerUserId} IS NULL)
      )`,
    ),
    check(
      "agency_white_label_storefront_listings_publish_semantics_ck",
      sql`(
        (${table.publishedAt} IS NULL AND ${table.publishedBy} IS NULL)
        OR (${table.publishedAt} IS NOT NULL
          AND ${table.publishedBy} IS NOT NULL
          AND ${table.status} <> 'rejected')
      )`,
    ),
  ],
);

export type AgencyWhiteLabelStorefrontListingRow =
  typeof agencyWhiteLabelStorefrontListings.$inferSelect;
export type NewAgencyWhiteLabelStorefrontListingRow =
  typeof agencyWhiteLabelStorefrontListings.$inferInsert;
